import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import type { CarrierAndService } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { assertNever } from "@redotech/util/type";
import { OutboundRate, RateProvider } from "./outbound-labels";

export type CarrierAccountIdentity = Pick<
  OutboundRate,
  "provider" | "carrier_account_id" | "providerInfo" | "aliasOfCarrierAccountId"
>;

type ComparableRate = CarrierAccountIdentity & CarrierAndService;

/** Providers whose accounts replace an EasyPost account of the same carrier. */
export type DirectCarrierProvider =
  | RateProvider.AMAZON_SHIPPING
  | RateProvider.GOFO;

interface DirectProvider {
  /** The carrier whose EasyPost account this provider replaces. */
  carrier: Carriers;
  providerInfoAlias: (
    providerInfo: OutboundRate["providerInfo"],
  ) => string | null | undefined;
}

function directProvider(provider: RateProvider): DirectProvider | null {
  switch (provider) {
    case RateProvider.AMAZON_SHIPPING:
      return {
        carrier: Carriers.AMAZON,
        providerInfoAlias: (providerInfo) =>
          providerInfo?.amazonShipping?.aliasOfCarrierAccountId,
      };
    case RateProvider.GOFO:
      return {
        carrier: Carriers.GOFO,
        providerInfoAlias: (providerInfo) =>
          providerInfo?.gofo?.aliasOfCarrierAccountId,
      };
    case RateProvider.SOAP_BOX:
    case RateProvider.EASYPOST:
    case RateProvider.UPS:
    case RateProvider.LANDMARK_GLOBAL:
    case RateProvider.REDO_LANDMARK_GLOBAL:
    case RateProvider.INTERNATIONAL_BRIDGE:
    case RateProvider.ETA_EXPRESS:
    case RateProvider.TEST_CARRIER:
    case RateProvider.FEDEX:
    case RateProvider.USPS:
    case RateProvider.DHL_ECOMMERCE:
    case RateProvider.GLOBALE:
    case RateProvider.TIKTOK:
    case RateProvider.CANADA_POST:
    case RateProvider.UNIUNI:
    case RateProvider.SENDCLOUD:
    case RateProvider.STALLION_EXPRESS:
    case RateProvider.ECOM_EXPRESS:
    case RateProvider.AMAZON_BUY_SHIPPING:
    case RateProvider.CHITCHATS:
    case RateProvider.FREIGHTWIRE:
    case RateProvider.WEGROW:
      return null;
    default:
      return assertNever(provider);
  }
}

export function isDirectCarrierProvider(
  provider: RateProvider,
): provider is DirectCarrierProvider {
  return directProvider(provider) !== null;
}

export function canonicalCarrierAccountId(
  rate: CarrierAccountIdentity,
): string {
  const direct = directProvider(rate.provider);
  if (!direct) {
    return rate.carrier_account_id;
  }
  return (
    direct.providerInfoAlias(rate.providerInfo) ||
    rate.aliasOfCarrierAccountId ||
    rate.carrier_account_id
  );
}

export function carrierAccountIdMatchesRate(
  savedCarrierAccountId: string | null | undefined,
  rate: CarrierAccountIdentity,
): boolean {
  if (!savedCarrierAccountId) {
    return false;
  }
  return (
    savedCarrierAccountId === rate.carrier_account_id ||
    savedCarrierAccountId === canonicalCarrierAccountId(rate)
  );
}

function isEasypostTwinOfDirectRate(
  easypost: ComparableRate,
  direct: ComparableRate,
): boolean {
  return (
    easypost.provider === RateProvider.EASYPOST &&
    easypost.carrier === direct.carrier &&
    directProvider(direct.provider)?.carrier === direct.carrier
  );
}

export function isAliasedDirectRate(
  a: ComparableRate,
  b: ComparableRate,
): boolean {
  return (
    a.service === b.service &&
    (isEasypostTwinOfDirectRate(a, b) || isEasypostTwinOfDirectRate(b, a)) &&
    canonicalCarrierAccountId(a) === canonicalCarrierAccountId(b)
  );
}
